import React, { useEffect, useState } from 'react';
import type { EngineStatus } from '@pagepilot/domain';
import { Badge, Card } from '../ui';
import { PageOverviewScreen } from './PageOverview';
import { ImagePostsScreen } from './ImagePosts';
import { ReelsScreen } from './Reels';
import { CalendarScreen } from './Calendar';
import { QueueScreen } from './Queue';
import { PublishedScreen } from './Published';
import { AnalyticsScreen } from './Analytics';
import { AssetLibraryScreen } from './AssetLibrary';
import { PromptLibraryScreen } from './PromptLibrary';
import { ActivityScreen } from './Activity';
import { ProvidersScreen } from './Providers';
import { ConnectForm, PageConfigForm } from './Pages';

export type PageScreen =
  | 'overview' | 'imagePosts' | 'reels' | 'calendar' | 'queue'
  | 'published' | 'analytics' | 'assets' | 'prompts' | 'activity' | 'providers' | 'settings';

const PAGE_NAV: { id: PageScreen; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'imagePosts', label: 'Image Posts' },
  { id: 'reels', label: 'Reels' },
  { id: 'calendar', label: 'Content Calendar' },
  { id: 'queue', label: 'Publishing Queue' },
  { id: 'published', label: 'Published' },
  { id: 'analytics', label: 'Performance' },
  { id: 'assets', label: 'Asset Library' },
  { id: 'prompts', label: 'Prompt Library' },
  { id: 'activity', label: 'Activity & Logs' },
  { id: 'providers', label: 'AI Providers' },
  { id: 'settings', label: 'Page Settings' },
];

interface PageDetail {
  id: string;
  internalLabel: string;
  status: string;
  connection: { connectionStatus: string } | null;
}

export function PageWorkspace(props: {
  pageId: string;
  pageLabel: string;
  status: EngineStatus | null;
  onBack: () => void;
}) {
  const { pageId, status } = props;
  const [screen, setScreen] = useState<PageScreen>('overview');
  const [detail, setDetail] = useState<PageDetail | null>(null);
  const [navOpen, setNavOpen] = useState(false); // mobile page-nav drawer

  const loadDetail = async () => {
    const pages = (await window.pagepilot.pages.list()) as PageDetail[];
    setDetail(pages.find((p) => p.id === pageId) ?? null);
  };
  useEffect(() => { void loadDetail(); }, [pageId]);

  const label = detail?.internalLabel ?? props.pageLabel;

  return (
    <div className="flex min-h-0 flex-1 flex-col md:flex-row">
      {/* Mobile page toolbar: back + current page + nav toggle */}
      <div className="flex items-center gap-2 border-b border-neutral-800 bg-neutral-950 px-3 py-2 md:hidden">
        <button onClick={props.onBack} className="rounded-lg px-2 py-1 text-xs text-neutral-400 hover:text-neutral-200" aria-label="All pages">←</button>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-100">{label}</span>
        <button onClick={() => setNavOpen((o) => !o)} className="rounded-lg p-1.5 text-neutral-300 hover:bg-neutral-800" aria-label="Toggle page menu">☰</button>
      </div>

      {/* Page-scoped sidebar (collapsible on mobile) */}
      <aside className={`${navOpen ? 'flex' : 'hidden'} w-full shrink-0 flex-col border-b border-neutral-800 bg-neutral-950 md:flex md:w-56 md:border-b-0 md:border-r`}>
        <button
          onClick={props.onBack}
          className="hidden items-center gap-2 px-4 py-3 text-left text-xs text-neutral-400 hover:text-neutral-200 md:flex"
        >
          <span aria-hidden>←</span> All Pages
        </button>
        <div className="border-t border-neutral-800 px-4 py-3">
          <p className="truncate text-sm font-semibold text-neutral-100">{label}</p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {detail && <Badge status={detail.status} />}
            {detail && <Badge status={detail.connection?.connectionStatus ?? 'DISCONNECTED'} />}
          </div>
        </div>
        <nav className="mt-1 flex-1 space-y-0.5 overflow-y-auto px-2 pb-3">
          {PAGE_NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => { setScreen(n.id); setNavOpen(false); }}
              className={`block w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                screen === n.id ? 'bg-brand-500/15 text-brand-300' : 'text-neutral-400 hover:bg-neutral-900 hover:text-neutral-200'
              }`}
            >
              {n.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Scoped content */}
      <main className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
        {screen === 'overview' && <PageOverviewScreen pageId={pageId} onNavigate={(s) => setScreen(s as PageScreen)} />}
        {screen === 'imagePosts' && <ImagePostsScreen pageId={pageId} />}
        {screen === 'reels' && <ReelsScreen pageId={pageId} />}
        {screen === 'calendar' && <CalendarScreen pageId={pageId} />}
        {screen === 'queue' && <QueueScreen pageId={pageId} />}
        {screen === 'published' && <PublishedScreen pageId={pageId} />}
        {screen === 'analytics' && <AnalyticsScreen pageId={pageId} />}
        {screen === 'assets' && <AssetLibraryScreen pageId={pageId} />}
        {screen === 'prompts' && <PromptLibraryScreen />}
        {screen === 'activity' && <ActivityScreen pageId={pageId} />}
        {screen === 'providers' && <ProvidersScreen status={status} />}
        {screen === 'settings' && (
          <div className="space-y-4">
            <h1 className="text-lg font-semibold">Page Settings</h1>
            <Card title="Facebook connection">
              <ConnectForm pageId={pageId} onDone={() => void loadDetail()} />
            </Card>
            <Card title="Page configuration">
              <PageConfigForm pageId={pageId} onDone={() => void loadDetail()} />
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
